import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { ShoppingCartOutlined, CloudOutlined, HomeOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Chicago = () => {
  const history = useHistory();

  useEffect(() => {
    const localSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Comfort Bears in Chicago - Lumi Bears", 
      "description": "Premium comfort companions for Chicago families. Emotional support bears designed to help children in Chicago with seasonal changes and emotional regulation.",
      "url": "https://lumibears.com/locations/chicago",
      "about": {
        "@type": "Product",
        "name": "Lumi Comfort Bears", 
        "description": "Emotional support companions for children"
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "City",
          "name": "Chicago",
          "containedInPlace": {
            "@type": "State",
            "name": "Illinois"
          }
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json'; 
    script.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script);

    document.title = "Comfort Bears Chicago - Emotional Support for Chicago Children | Lumi Bears";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium comfort bears for Chicago families. Help Chicago children with seasonal changes and emotional support. Fast shipping to Loop, Lincoln Park, Wicker Park.');
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('chicago')) {
        existingScript.remove();
      }
      document.title = "Lumi Bears - Comfort Companions for Sensitive Children | Emotional Support Teddy Bears";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Premium comfort companions for children who need extra emotional support. Soft, intelligent teddy bears designed to help kids feel calm and secure during bedtime and challenging moments.');
      }
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={1} style={{ color: 'white', marginBottom: '20px', fontSize: '2.5rem' }}>
                Comfort Bears in Chicago
              </Title>
              <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                Supporting Chicago families with premium comfort companions designed for children who need 
                extra emotional support. From Lincoln Park to River North, Wicker Park to Hyde Park - 
                helping your child thrive through every Chicago season.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/')}
                style={{ 
                  background: 'white', 
                  borderColor: 'white', 
                  color: '#667eea',
                  height: '50px',
                  fontSize: '16px'
                }}
              >
                Shop Comfort Bears
              </Button>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <CloudOutlined style={{ fontSize: '120px', color: 'rgba(255,255,255,0.3)' }} />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Why Chicago Families Choose Lumi Bears */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={16}>
            <Title level={2} style={{ color: '#333', marginBottom: '24px' }}>
              Why Chicago Families Trust Lumi Bears
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '20px' }}>
              Chicago's dramatic seasonal changes and vibrant urban energy create unique opportunities and challenges 
              for families. From long winters to busy summers, competitive schools to diverse neighborhoods, 
              Chicago parents need comfort solutions that help children stay emotionally regulated through all of life's changes.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666' }}>
              Whether you're in downtown condos, suburban neighborhoods, or historic areas, our comfort bears provide 
              consistent emotional support that helps Chicago children build resilience and confidence through 
              every season and life transition.
            </Paragraph>
          </Col>
          <Col xs={24} lg={8}>
            <Card style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <HomeOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#333' }}>Built for Chicago</Title>
              <Text style={{ color: '#666' }}>
                Designed to support children through Chicago's seasonal transitions and dynamic city experiences.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Shipping & Delivery Info */}
        <Card style={{ marginBottom: '40px', background: '#f0f8ff' }}>
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '48px', color: '#667eea' }} />
            </Col>
            <Col xs={24} md={20}>
              <Title level={3} style={{ color: '#333', marginBottom: '16px' }}>
                Fast Delivery to Chicago Area
              </Title>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Downtown & Loop:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>North Side:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>South & West Side:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: '16px', color: '#666', marginBottom: 0 }}>
                <Text italic>
                  All orders ship with tracking. Current delivery time reflects our pre-order production schedule 
                  as we carefully craft each comfort companion for Midwest families.
                </Text>
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* Chicago-Specific Challenges */}
        <Title level={2} style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
          Supporting Chicago Children Through City Life
        </Title>
        
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Seasonal Transitions</Title>
              <Paragraph style={{ color: '#666' }}>
                Chicago's dramatic weather changes can affect children's mood and energy levels. Comfort bears provide 
                consistent emotional support during difficult transitions from bright summers to long winters.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Academic Pressure</Title>
              <Paragraph style={{ color: '#666' }}>
                Chicago's competitive school environment can create stress for children. Comfort bears offer 
                emotional support for homework challenges, test anxiety, and school social situations.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Urban Intensity</Title>
              <Paragraph style={{ color: '#666' }}>
                City life with its sounds, crowds, and constant activity can be overwhelming for sensitive children. 
                Comfort bears provide a calming anchor in Chicago's energetic urban environment.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Family Resources for Chicago */}
        <Card style={{ marginBottom: '40px' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Resources for Chicago Families
          </Title>
          <Row gutter={[32, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Child Development Support</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.luriechildrens.org/en/specialties-conditions/behavioral-health/" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Lurie Children's Hospital - Behavioral Health
                  </a>
                </li>
                <li>
                  <a href="https://www.cps.edu/services-and-supports/student-health-and-wellness/mental-health/" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Chicago Public Schools - Mental Health Services
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Family Activities & Wellness</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.chicagoparkdistrict.com/parks-facilities/programs" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Chicago Parks - Family Programs
                  </a>
                </li>
                <li>
                  <a href="https://www.chipublib.org/programs-and-partnerships/programs-for-kids/" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Chicago Public Library - Children's Programs
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Neighborhoods We Serve */}
        <Card style={{ marginBottom: '40px', background: '#f5f5f5' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Serving Chicago Neighborhoods & Communities
          </Title>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>North Side:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Lincoln Park</li>
                <li>Lakeview</li>
                <li>Wicker Park</li>
                <li>Logan Square</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>Downtown:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Loop</li>
                <li>River North</li>
                <li>Streeterville</li>
                <li>West Loop</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>South Side:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Hyde Park</li>
                <li>Bronzeville</li>
                <li>Kenwood</li>
                <li>Bridgeport</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>West Side:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Bucktown</li>
                <li>Ukrainian Village</li>
                <li>Humboldt Park</li>
                <li>Little Village</li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Call to Action */}
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '12px'
        }}>
          <Title level={2} style={{ color: '#4a148c', marginBottom: '20px' }}>
            Ready to Support Your Chicago Child?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6a1b9a', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
            Join Chicago families who are giving their children the emotional support tools they need to thrive 
            in the Windy City through every season and challenge.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            icon={<ShoppingCartOutlined />}
            onClick={() => history.push('/')}
            style={{ 
              background: '#4a148c', 
              borderColor: '#4a148c',
              height: '50px',
              fontSize: '18px',
              padding: '0 30px'
            }}
          >
            Shop Comfort Bears
          </Button>
        </div>

      </div>
    </div>
  );
};

export default Chicago;